import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthActions,
  AuthSelectors,
  ExperimentsActions,
  FirebaseActionTypes,
  FirebaseActions,
  LanguageService,
  NotificationActions,
  SentryService,
} from '@web_opxp/features';
import { all, call, delay, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { ApiService, HttpError } from 'web_core_library';
import { detectLandingPageTracking } from '../base/cookieService';
import ENV from '../base/env';
import * as HistoryService from '../base/historyService';
import { getCurrentSearchParams, redirectLogin } from '../base/historyService';
import ManagementService from './managementService';
import { ManageActions } from './slice';
import {
  IChargebeeSubscription,
  ISubscription,
  ITerminateSubscriptionPayload,
  TGetSubscriptionResponse,
  TSubscriptionID,
} from './types';

export function* handleAppStart() {
  // Init ApiService
  yield call(ApiService.init, ENV.ApiUrl);

  // init firebase
  const searchParams: URLSearchParams = yield call(getCurrentSearchParams);
  yield put(
    FirebaseActions.firebaseInitAction(
      ENV.FirebaseConfig.apiKey,
      ENV.FirebaseConfig.projectId,
      ENV.FirebaseConfig.appId,
      ENV.FirebaseConfig.messagingSenderId,
      ENV.FirebaseConfig.measurementId,
      searchParams
    )
  );
  yield take(FirebaseActionTypes.FIREBASE_INITIALIZED);
  yield call(detectLandingPageTracking);
  yield put(FirebaseActions.firebaseStartAction({ analytics: true, config: true, messaging: false }));

  // start session token restoration
  yield put(
    AuthActions.authRestore({
      getCurrentSearchParams: HistoryService.getCurrentSearchParams,
      exchangeAccessToken: HistoryService.exchangeAccessToken,
      updateSearchParams: HistoryService.updateSearchParams,
      refreshToken: HistoryService.refreshToken,
      redirectLogin: HistoryService.redirectLogin,
      redirectLogout: HistoryService.redirectLogout,
    })
  );
  const authResult = yield take([AuthActions.restoreAuthSuccess, AuthActions.restoreAuthFail]);
  if (authResult.type !== AuthActions.restoreAuthSuccess.type) {
    yield call(redirectLogin);
    return;
  }
  const userId = yield select(AuthSelectors.getUserId);
  // set user id for tracking
  yield put(FirebaseActions.setUserAction(userId));
  // load experiments feature as soon as possible
  yield call(loadExperimentsFeature);
  yield call(ManagementService.init, ApiService);
  const subscriptions: ISubscription[] = yield call(handleFetchingOfSubscriptions);
  yield call(checkForLegacySubscriptions, subscriptions, userId);
  yield put(ManageActions.manageAppStarted(subscriptions));
}

export function* checkForLegacySubscriptions(subscriptions: ISubscription[], userId: number) {
  for (const subscription of subscriptions) {
    if ((subscription as IChargebeeSubscription).isChargeBee) {
      // if subscriptions is chargbee - everything OK
      continue;
    }
    // log legacy subscription on sentry as defined in BW-7422
    yield call(console.warn, 'Legacy subscription found!');
    yield call(SentryService.captureMessage, 'Legacy subscription found!', { subscriptionId: subscription.id, userId });
  }
}

export function* loadExperimentsFeature() {
  const experiments = [];
  yield put(ExperimentsActions.initExperiments({ experiments }));
  yield take(ExperimentsActions.experimentsReady);
}

export function* handleFetchingOfSubscriptions() {
  let subscriptions: ISubscription[] = [];
  try {
    const result: TGetSubscriptionResponse = yield call(ManagementService.getSubscriptions);
    subscriptions = result.data.subscriptions;
  } catch {
    // ignore failing request and treat subscriptions as empty
  }
  return subscriptions;
}

export function* handleTerminateSubscriptionSaga(action: PayloadAction<ITerminateSubscriptionPayload>) {
  const { subscriptionId, terminate } = action.payload;
  try {
    yield call(ManagementService.terminateSubscription, subscriptionId, terminate);
    yield delay(300);
    const successMessage = LanguageService.getTFunction()('manage:Subscriptions.Notifications.terminationSuccess');
    yield put(NotificationActions.showSuccess(successMessage));
    const subscriptions: ISubscription[] = yield call(handleFetchingOfSubscriptions);
    yield put(ManageActions.manageAppStarted(subscriptions));
  } catch (error) {
    yield put(NotificationActions.showError((error as Error).message));
  }
}

export function* handleRestoreSubscriptionSaga(action: PayloadAction<TSubscriptionID>) {
  const subscriptionId = action.payload;
  try {
    yield call(ManagementService.reactivateUserSubscription, subscriptionId);
    yield delay(300);
    yield put(ManageActions.restoreSubscriptionSuccess());
    const subscriptions: ISubscription[] = yield call(handleFetchingOfSubscriptions);
    yield put(ManageActions.manageAppStarted(subscriptions));
  } catch (error) {
    if ((error as HttpError).status === 422) {
      const alertMessage = LanguageService.getTFunction()('manage:Manage.Restore.Error.InProgress');
      yield put(NotificationActions.showError(alertMessage));
      yield put(ManageActions.restoreSubscriptionFailure());
      return;
    }
    yield put(NotificationActions.showError((error as Error).message));
    yield put(ManageActions.restoreSubscriptionFailure());
  }
}

export default function* watchManageApp() {
  yield all([
    takeEvery(ManageActions.startManageApp, handleAppStart),
    takeLeading(ManageActions.terminateSubscriptionRequest, handleTerminateSubscriptionSaga),
    takeLeading(ManageActions.restoreSubscription, handleRestoreSubscriptionSaga),
  ]);
}
